import { IPresentableEntity, EntityMap, EntityChildrenMap, IParentChildrenCluster } from '@/models/available-entities.interface';
import { CampaignListingEntityDto, OrganisationDegrees } from '@studyportals/campaign-management-api-interface/src/domain/campaign';

class AvailableEntitiesPresenter {
	private sortedEntities: IPresentableEntity[] = [];
	private relevantParentIdsMap: Map<number, number[]> = new Map();
	private parentMap: EntityMap = {};
	private parentChildrenMap: EntityChildrenMap = {};
	private parentChildrenCluster: IParentChildrenCluster[] = [];
	private initialEntityGroups: IPresentableEntity[][] = [];
	private finalEntityGroups: IPresentableEntity[][] = [];
	private indentationLevelBeingChecked = 0;
	private maxIndentationLevel = 0;

	public convertToPresentableEntities(items: CampaignListingEntityDto[]): IPresentableEntity[] {
		return items.map((item) => {
			const relevantChild = items.find(possibleChild => possibleChild.parent === item.id);
			const isOrganisation = item.degreeLevel === OrganisationDegrees.ORGANISATION || item.degreeLevel === OrganisationDegrees.SUBORGANISATION;
			let indentationLevel = 0;
			let parent = item.parent;

			const relevantParentIds: number[] = [];

			while (parent !== null) {
				relevantParentIds.push(parent);
				indentationLevel++;
				const foundParent = items.find(possibleParent => possibleParent.id === parent);
				if (!foundParent) {
					parent = null;
					continue;
				}

				parent = foundParent.parent;
			}

			this.relevantParentIdsMap.set(item.id, relevantParentIds);

			return {
				...item,
				hasChildren: relevantChild !== undefined,
				hideChildren: false,
				indentationLevel,
				isOrganisation,
				relevantParentIndexes: []
			};
		});
	}

	/* The following rules are applied when sorting the entities:
	- a parent should always come first (before its children, grandchildren, greatgrandchildren, etc).
	- a child or group of children is always put immediately after their parent.
	- when items have the same "indentationLevel" value, items with no children come first.
	- when items have the same "indentationLevel" and "hasChildren" value, items with an "isOrganisation" value of "false" come first.
	- when items have the same "indentationLevel", "hasChildren", and "isOrganisation" value, items with a lower "id" come first. */

	public sortPresentableEntities(items: IPresentableEntity[]): IPresentableEntity[] {
		this.sortedEntities = items;

		this.resetRelevantProperties();
		this.ensureThatHighestLevelOrganisationsAreNotDisplayedAsSuborganisations();
		this.ensureThatSubOrganisationsWithoutParentsAreNotIndentedAsSuborganisations();
		this.prepareParentMap();
		this.prepareParentChildrenMap();
		this.sortChildrenOfParentChildrenMap();
		this.prepareParentChildrenCluster();
		this.enrichParentChildrenClusterWithDeeperLevelParents();
		this.prepareInitialEntityGroups();
		this.prepareFinalEntityGroups();
		this.flattenFinalEntityGroups();
		this.setRelevantParentIndexes();
		return this.sortedEntities;
	}

	private resetRelevantProperties(): void {
		this.parentMap = {};
		this.parentChildrenMap = {};
		this.parentChildrenCluster = [];
		this.initialEntityGroups = [];
		this.finalEntityGroups = [];
		this.indentationLevelBeingChecked = 0;
		this.maxIndentationLevel = 0;
	}

	private ensureThatHighestLevelOrganisationsAreNotDisplayedAsSuborganisations(): void {
		const minIndentationLevel = Math.min(...this.sortedEntities.map(item => item.indentationLevel));
		if (minIndentationLevel === 0) {
			return;
		}

		this.sortedEntities.forEach(item => {
			item.indentationLevel -= minIndentationLevel;
			if (item.indentationLevel > 0) {
				return;
			}

			item.parent = null;
		});
	}

	private ensureThatSubOrganisationsWithoutParentsAreNotIndentedAsSuborganisations(): void {
		const minIndentationLevel = Math.min(...this.sortedEntities.map(item => item.indentationLevel));
		const wronglyIndentedSubOrganisations = this.sortedEntities.filter(item => {
			return item.isOrganisation &&
				item.indentationLevel !== minIndentationLevel &&
				this.sortedEntities.find(possibleParent => possibleParent.id === item.parent) === undefined;
		});

		wronglyIndentedSubOrganisations.forEach(subOrganisation => {
			this.sortedEntities.forEach(item => {
				const relevantParentIds = this.relevantParentIdsMap.get(item.id);
				if (!relevantParentIds) {
					return;
				}

				const wronglyIndentedParent = relevantParentIds.find(id => subOrganisation.id === id);
				if (!wronglyIndentedParent) {
					return;
				}

				item.indentationLevel -= 1;
			});

			subOrganisation.indentationLevel -= 1;
		});
	}

	private prepareParentMap(): void {
		this.parentMap = this.sortedEntities.reduce((acc: EntityMap, item): EntityMap => {
			acc[item.id] = item;
			return acc;
		}, {});
	}

	private prepareParentChildrenMap(): void {
		this.parentChildrenMap = {};
		const parentsWithoutChildren: number[] = [];
		const entitiesWithoutChildren = this.sortedEntities.filter(item => !item.hasChildren);

		this.parentChildrenMap = entitiesWithoutChildren.reduce((acc: EntityChildrenMap, item: IPresentableEntity): EntityChildrenMap => {
			if (!item.parent) {
				parentsWithoutChildren.push(item.id);
				return {};
			}

			if (!acc[item.parent]) {
				acc[item.parent] = [];
			}

			acc[item.parent].push(item);
			return acc;
		}, {});

		parentsWithoutChildren.forEach((parentId) => {
			this.parentChildrenMap[parentId] = [];
		});
	}

	private sortChildrenOfParentChildrenMap(): void {
		Object.keys(this.parentChildrenMap).forEach(parentId => {
			this.parentChildrenMap[parentId].sort((entityA: IPresentableEntity, entityB: IPresentableEntity) => {
				if (entityA.isOrganisation !== entityB.isOrganisation) {
					// Entities with "isOrganisation" "false" come before entities with "isOrganisation" "true".
					return entityA.isOrganisation ? 1 : -1;
				}

				// Sort by "id" in ascending order.
				return entityA.id - entityB.id;
			});
		});
	}

	private prepareParentChildrenCluster(): void {
		this.maxIndentationLevel = Math.max(...this.sortedEntities.map(item => item.indentationLevel));
		if (this.maxIndentationLevel === 0) {
			this.parentChildrenCluster = this.sortedEntities.map(item => ({
				parent: item,
				children: []
			}));
			return;
		}

		this.parentChildrenCluster = Object.keys(this.parentChildrenMap)
			.map(parentId => ({
				parent: this.parentMap[parentId],
				children: this.parentChildrenMap[parentId]
			}));
	}

	private enrichParentChildrenClusterWithDeeperLevelParents(): void {
		const allParentIds = new Set([
			...this.sortedEntities
				.filter((item) => item.hasChildren)
				.map((item) => item.id)
		]);

		allParentIds.forEach(parentId => {
			if (this.parentChildrenCluster.some(item => item.parent && item.parent.id === parentId)) {
				return;
			}

			this.parentChildrenCluster.push({
				parent: this.parentMap[parentId],
				children: []
			});
		});
	}

	private prepareInitialEntityGroups(): void {
		this.initialEntityGroups = this.parentChildrenCluster
			.map(obj => [obj.parent, ...obj.children])
			.map(obj => {
				if (obj[0]) {
					return obj;
				}

				return obj.slice(1);
			});
	}

	private prepareFinalEntityGroups(): void {
		while (this.indentationLevelBeingChecked <= this.maxIndentationLevel) {
			this.addFinalEntityGroupsForNextLevel();
		}
	}

	private addFinalEntityGroupsForNextLevel(): void {
		const groupsForThisIndentationLevel = this.initialEntityGroups
			.filter(group => {
				const parentOfGroup = group[0];
				return parentOfGroup.indentationLevel === this.indentationLevelBeingChecked
			})
			.sort((groupA, groupB) => {
				const parentOfGroupA = groupA[0];
				const parentOfGroupB = groupB[0];
				if (this.indentationLevelBeingChecked === 0) {
					return parentOfGroupA.id - parentOfGroupB.id;
				} else {
					return parentOfGroupB.id - parentOfGroupA.id;
				}
			});

		groupsForThisIndentationLevel.forEach(group => {
			this.addGroupInRightPlaceOfFinalEntityGroups(group);
		});

		this.indentationLevelBeingChecked++;
	}

	private addGroupInRightPlaceOfFinalEntityGroups(group: IPresentableEntity[]): void {
		const parentOfGroup = group[0];
		const indexOfGroupAfterWhichToPlaceThisGroup = this.finalEntityGroups.findIndex(finalGroup => {
			return finalGroup.find(entity => entity.id === parentOfGroup.parent) !== undefined;
		});
		if (indexOfGroupAfterWhichToPlaceThisGroup === -1) {
			this.finalEntityGroups.push(group);
			return;
		}

		this.finalEntityGroups.splice(indexOfGroupAfterWhichToPlaceThisGroup + 1, 0, group);
	}

	private flattenFinalEntityGroups(): void {
		this.sortedEntities = this.finalEntityGroups.flat();
	}

	private setRelevantParentIndexes(): void {
		const parentIndexMap: Map<number, number> = new Map();

		this.sortedEntities.forEach((item, entityIndex) => {
			/* Since an item's parents are always higher up in the array,
			the parent index map be built up inside the same loop as where it's used. */
			parentIndexMap.set(item.id, entityIndex);

			const relevantParentIds = this.relevantParentIdsMap.get(item.id);
			item.relevantParentIndexes = [];
			if (!relevantParentIds) {
				return;
			}

			relevantParentIds.forEach((parent) => {
				const parentIndex = parentIndexMap.get(parent) as number;
				if (parentIndex === undefined) {
					return;
				}

				item.relevantParentIndexes.push(parentIndex);
			});
		});
	}
}

export default new AvailableEntitiesPresenter();
